/**
 * SAKPIGrid — 9 expandable KPI cards in 3x3 grid for the Superadmin cockpit
 * Pattern: ExpandableKPI from RadarPage (framer-motion accordion)
 */
import React from 'react';
import { Box, Typography, Chip } from '@mui/material';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import BusinessIcon from '@mui/icons-material/Business';
import TagIcon from '@mui/icons-material/Tag';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import DescriptionIcon from '@mui/icons-material/Description';
import ExpandableKPI from '../shared/ExpandableKPI';
import { colors } from '../../styles/colors';

const getScoreColor = (score) => {
  if (score >= 80) return colors.success;
  if (score >= 65) return colors.primary;
  if (score >= 50) return colors.warning;
  return colors.danger;
};

// --- Expand content builders ---

function MRRBreakdown({ agencies }) {
  if (!agencies || agencies.length === 0) return <EmptyDetail />;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {agencies.map((a) => (
        <Box key={a.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: a.accent_color || colors.primary, flexShrink: 0 }} />
            <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.82rem', color: colors.charcoal }}>
              {a.name}
            </Typography>
          </Box>
          <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.82rem', fontWeight: 600, color: colors.charcoal }}>
            {(a.mrr || 0).toLocaleString('fr-FR')} EUR
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

function AgenciesDetail({ agencies }) {
  if (!agencies || agencies.length === 0) return <EmptyDetail />;
  const paymentLabel = { paid: 'Paye', due: 'A payer', overdue: 'En retard' };
  const paymentColor = { paid: colors.successDark, due: colors.warningDark, overdue: colors.dangerDark };
  const paymentBg = { paid: colors.successBg, due: colors.warningBg, overdue: colors.dangerBg };
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
      {agencies.map((a) => (
        <Box key={a.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.82rem', color: colors.charcoal }}>
            {a.name}
          </Typography>
          <Chip
            label={paymentLabel[a.payment_status] || 'A payer'}
            size="small"
            sx={{
              fontFamily: '"DM Sans", sans-serif', fontSize: '0.65rem', fontWeight: 600,
              bgcolor: paymentBg[a.payment_status] || paymentBg.due,
              color: paymentColor[a.payment_status] || paymentColor.due,
              borderRadius: '100px', height: 20,
            }}
          />
        </Box>
      ))}
    </Box>
  );
}

function BrandsDetail({ agencies }) {
  if (!agencies || agencies.length === 0) return <EmptyDetail />;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
      {agencies.map((a) => (
        <Box key={a.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.82rem', color: colors.charcoal }}>
            {a.name}
          </Typography>
          <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.82rem', fontWeight: 600, color: colors.textMed }}>
            {a.brands_count} / {a.brands_max}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

function CrisesDetail({ agencies }) {
  if (!agencies || agencies.length === 0) return <EmptyDetail text="Aucune crise active" />;
  const withCrises = agencies.filter(a => a.crises_active > 0);
  if (withCrises.length === 0) {
    return <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.82rem', color: colors.success }}>Aucune crise active</Typography>;
  }
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
      {withCrises.map((a) => (
        <Box key={a.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.82rem', color: colors.charcoal }}>
            {a.name}
          </Typography>
          <Chip
            label={`${a.crises_active} crise${a.crises_active > 1 ? 's' : ''}`}
            size="small"
            sx={{
              fontFamily: '"DM Sans", sans-serif', fontSize: '0.65rem', fontWeight: 600,
              bgcolor: colors.dangerBg, color: colors.danger, borderRadius: '100px', height: 20,
            }}
          />
        </Box>
      ))}
    </Box>
  );
}

function ActionsDetail({ agencies }) {
  if (!agencies || agencies.length === 0) return <EmptyDetail />;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
      {agencies.map((a) => {
        const total = (a.brands || []).reduce((s, b) => s + (b.actions_pending || 0), 0);
        return (
          <Box key={a.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.82rem', color: colors.charcoal }}>
              {a.name}
            </Typography>
            <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.82rem', fontWeight: 600, color: total > 0 ? colors.primary : colors.textLight }}>
              {total} en attente
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}

function ScoreDetail({ agencies }) {
  if (!agencies || agencies.length === 0) return <EmptyDetail />;
  const sorted = [...agencies].sort((a, b) => (b.score_avg || 0) - (a.score_avg || 0));
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
      {sorted.map((a) => (
        <Box key={a.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.82rem', color: colors.charcoal }}>
            {a.name}
          </Typography>
          <Typography sx={{
            fontFamily: '"Playfair Display", Georgia, serif', fontSize: '0.88rem',
            fontWeight: 500, color: getScoreColor(a.score_avg || 0),
          }}>
            {(a.score_avg || 0).toFixed(1)}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

function EmptyDetail({ text }) {
  return (
    <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.82rem', color: colors.textLight }}>
      {text || 'Aucune donnee'}
    </Typography>
  );
}

function SAKPIGrid({ kpis, agencies }) {
  if (!kpis) return null;

  const hasCrisis = kpis.crises_active > 0;
  const churn = kpis.churn_rate || 0;
  const completed = kpis.actions_completed || 0;
  const total = kpis.actions_total || 0;
  const pct = total > 0 ? Math.round((completed / total) * 100) : 0;
  const cost = kpis.ai_cost_monthly || 0;
  const mrr = kpis.mrr_total || 0;

  const cards = [
    {
      icon: <AttachMoneyIcon sx={{ fontSize: '1.1rem' }} />,
      label: 'MRR Total',
      value: `${mrr.toLocaleString('fr-FR')} EUR`,
      summary: `${kpis.agencies_active || 0} agences actives`,
      info: 'Revenu mensuel recurrent total de toutes les agences sur la plateforme.',
      expandedContent: <MRRBreakdown agencies={agencies} />,
    },
    {
      icon: <BusinessIcon sx={{ fontSize: '1.1rem' }} />,
      label: 'Agences actives',
      value: `${kpis.agencies_active || 0}`,
      summary: `sur ${kpis.total_agencies || kpis.agencies_active || 0} agences`,
      info: 'Nombre d\'agences ayant au moins une marque active.',
      expandedContent: <AgenciesDetail agencies={agencies} />,
    },
    {
      icon: <TagIcon sx={{ fontSize: '1.1rem' }} />,
      label: 'Marques suivies',
      value: `${kpis.brands_count || 0}`,
      summary: `sur ${kpis.agencies_active || 0} agences`,
      info: 'Nombre total de marques surveillees sur la plateforme.',
      expandedContent: <BrandsDetail agencies={agencies} />,
    },
    {
      icon: <WarningAmberIcon sx={{ fontSize: '1.1rem' }} />,
      label: 'Crises actives',
      value: `${kpis.crises_active || 0}`,
      summary: hasCrisis ? 'Attention requise' : 'Aucune crise',
      info: 'Crises reputationnelles en cours non resolues.',
      expandedContent: <CrisesDetail agencies={agencies} />,
    },
    {
      icon: <CheckCircleOutlineIcon sx={{ fontSize: '1.1rem' }} />,
      label: 'Actions realisees',
      value: `${completed} / ${total}`,
      summary: `${pct}% completees`,
      info: 'Ratio global des actions de communication completees vs totales.',
      expandedContent: <ActionsDetail agencies={agencies} />,
    },
    {
      icon: <SmartToyIcon sx={{ fontSize: '1.1rem' }} />,
      label: 'Cout IA mensuel',
      value: `${cost.toFixed(0)} $`,
      summary: mrr > 0 ? `${(cost / mrr * 100).toFixed(1)}% du MRR` : '--',
      info: 'Cout total des appels IA (Claude, Perplexity, Apify) ce mois.',
      expandedContent: <EmptyDetail text="Detail par provider disponible dans l'onglet Couts API" />,
    },
    {
      icon: <TrendingUpIcon sx={{ fontSize: '1.1rem' }} />,
      label: 'Score moyen',
      value: `${(kpis.score_avg || 0).toFixed(1)}`,
      valueLabel: '/100',
      summary: null,
      info: 'Moyenne des scores de reputation de toutes les marques.',
      expandedContent: <ScoreDetail agencies={agencies} />,
    },
    {
      icon: <TrendingDownIcon sx={{ fontSize: '1.1rem' }} />,
      label: 'Taux de churn',
      value: `${(churn * 100).toFixed(1)}%`,
      summary: 'sur 3 mois glissants',
      info: 'Proportion d\'agences ayant annule leur abonnement sur les 3 derniers mois.',
      expandedContent: <EmptyDetail text={churn > 0 ? 'Agences perdues sur la periode' : 'Aucun churn sur la periode'} />,
    },
    {
      icon: <DescriptionIcon sx={{ fontSize: '1.1rem' }} />,
      label: 'Rapports envoyes',
      value: `${kpis.reports_sent || 0}`,
      summary: 'ce mois',
      info: 'Nombre de rapports de reputation envoyes aux clients ce mois.',
      expandedContent: <EmptyDetail text="Consultez l'activite recente pour le detail" />,
    },
  ];

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
        gap: 3,
        mb: 4,
      }}
    >
      {cards.map((card, index) => (
        <ExpandableKPI
          key={index}
          icon={card.icon}
          label={card.label}
          value={card.value}
          valueLabel={card.valueLabel}
          summary={card.summary}
          info={card.info}
          delay={0.05 * index}
          expandedContent={card.expandedContent}
        />
      ))}
    </Box>
  );
}

export default SAKPIGrid;
